// mission-run-config-preflight.ts
//
// H9 (audit Sol 2026-09-28): `run` checks the mission's OWN files before any
// dispatch, so a config mistake surfaces as `config_invalid: <file/field>`
// instead of "✗ Lỗi không xử lý được: [". Measured before this: a ticket file
// `{}` died inside the loader with a bare Zod dump, an unknown adapter inside
// the prompt builder, and a missing/corrupt verification profile only when
// the verifier read it — AFTER the worker session had run (and been paid
// for), leaving the ticket `in_progress`. Nothing creates the profile (it is
// part of the repo), and every attempt's verification reads it, so it is
// required up front.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { resolveCommandAdapter } from "./command-adapter.js";
import { TicketSchema } from "./schemas/ticket-schema.js";
import { VerificationProfileSchema } from "./schemas/verification-schema.js";
import { ticketUnrunnableInV1Reason, unrunnableTicketWayOut } from "./ticket-v1-runnability.js";

function message(err: unknown): string {
  return (err instanceof Error ? err.message : String(err)).slice(0, 400);
}

async function ticketProblem(ticketsDir: string, file: string): Promise<string | undefined> {
  let parsed: ReturnType<typeof TicketSchema.safeParse>;
  try {
    parsed = TicketSchema.safeParse(JSON.parse(await fs.readFile(join(ticketsDir, file), "utf8")));
  } catch (err) {
    return `.avatar/tickets/${file}: không đọc/parse được JSON (${message(err)})`;
  }
  if (!parsed.success) {
    return `.avatar/tickets/${file}: không khớp ticket schema (${message(parsed.error)})`;
  }
  try {
    resolveCommandAdapter(parsed.data.command_adapter);
  } catch {
    return `ticket ${parsed.data.id} (.avatar/tickets/${file}): command_adapter "${parsed.data.command_adapter}" không có trong bảng adapter`;
  }
  // H14: a hand-written file or a mission from an older CLI can still hold a
  // ticket v1 cannot dispatch — stop before the first attempt and say how
  // to get out (the ticket would otherwise fail three times, then block).
  const unrunnable = ticketUnrunnableInV1Reason(parsed.data);
  if (unrunnable !== undefined) {
    return `.avatar/tickets/${file}: ${unrunnable}. ${unrunnableTicketWayOut(parsed.data)}`;
  }
  return undefined;
}

/** First problem found in the mission's tickets / verification profile, or `undefined`. */
export async function findMissionRunConfigProblem(repoDir: string): Promise<string | undefined> {
  const ticketsDir = join(repoDir, ".avatar", "tickets");
  const files = (await fs.readdir(ticketsDir).catch(() => [] as string[]))
    .filter((f) => f.endsWith(".json"))
    .sort();
  for (const file of files) {
    const problem = await ticketProblem(ticketsDir, file);
    if (problem !== undefined) return problem;
  }

  const profilePath = ".avatar/verification-profile.json";
  try {
    const raw = await fs.readFile(join(repoDir, profilePath), "utf8");
    const parsed = VerificationProfileSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return `${profilePath}: không khớp schema (${message(parsed.error)})`;
  } catch (err) {
    return `${profilePath}: thiếu hoặc không parse được (${message(err)}) — commit profile vào repo trước khi run`;
  }
  return undefined;
}
